import { useState } from 'react';

import {
  ArrowButton, Button, DatePicker,
  TextField, IconWrapper,
  Tooltip, Select, Modal, Typography
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as InfoIcon } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { useValidation } from '@king-ict/eupisi-ui/hooks';
import { CodeBook } from '@king-ict/eupisi-ui/models';
import { codeBookToSelectData, nameof } from '@king-ict/eupisi-ui/utils';
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { createProgram, editProgram } from '../../api/nisooApi';
import StaticField from '../../components/StaticField';
import { useInput } from "../../hooks";
import { ProgramNameValidationScheme, QualificationType } from '../../models/CodeBook';
import { ProgramCodeBook, programInitial, ProgramType } from '../../models/Program';
import {
  useGetProgramTypesQuery,
  useGetEducationSectorsQuery,
  useGetEducationSubSectorsQuery,
  useGetLanguagesQuery,
  useGetQualificationTypesByProgramQuery
}
  from "../../queries/codeBookQuery";
import { codebookRouteProgramName } from '../../routes/routes';
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookProgramNameEdit(): JSX.Element {
  const history = useHistory();
  const { handleChange } = useInput();
  const location = useLocation();
  const { t } = useTranslation();

  const getProgramInitial = () => {
    if ((location.state as any).programName) {
      return (location.state as any).programName as ProgramCodeBook;
    }
    return programInitial;
  }

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [programName, setProgramName] = useState<ProgramCodeBook>(getProgramInitial());
  const { data: programTypes } = useGetProgramTypesQuery();
  const { data: educationSectors } = useGetEducationSectorsQuery();
  const { data: educationSubSectors } = useGetEducationSubSectorsQuery(
    programName.educationSectorId!,
    {
      skip: !programName.educationSectorId
    }
  );
  const { data: qualificationTypes } = useGetQualificationTypesByProgramQuery(programName.programTypeId!, {
    skip: programName.programTypeId == null,
  });
  const [hkoLevels, setHkoLevels] = useState<CodeBook[]>([]);

  const isProgramEditMode = programName && programName.id > 0;
  const { data: languages } = useGetLanguagesQuery();

  const [errors, doValidations] = useValidation({
    data: programName,
    validationSchema: ProgramNameValidationScheme,
  });

  const filteredProgramTypes = (): CodeBook[] => {

    if (programTypes && programTypes.length > 0) {
      return programTypes.filter(pt => pt.id === ProgramType.TrainingPrograms ||
        pt.id === ProgramType.CraftsAndArtsTrainingPrograms ||
        pt.id === ProgramType.CraftsSpecializationPrograms ||
        pt.id === ProgramType.RefinementPrograms ||        
        pt.id === ProgramType.NewVocationalCurriculum);
    }
    return [];
  }

  const handleProgramTypeChange = (event: any) => {
    const programTypeId = event.target.value;

    setProgramName((prev) => ({
      ...prev,
      programTypeId: programTypeId,
      qualificationTypeId: 0,
      hkoLevelId: 0
    }));
  };

  const handleQualificationTypeChange = (event: any) => {
    const qualificationTypeId = event.target.value;
    var hkoLevel = [];

    if (qualificationTypes) {
      for (let i = 0; i < qualificationTypes.length; i++) {
        if (qualificationTypes[i].id === qualificationTypeId) {
          hkoLevel.push(qualificationTypes[i].hkoLevel);
        }
      }
    }

    setHkoLevels(hkoLevel);

    var hkoLevelId = hkoLevel.length === 1 ? hkoLevel[0].id : 0;
    var hkoLevelName = hkoLevelId > 0 && hkoLevel.length === 1 ? hkoLevel[0].name : "";

    var educationSubSectorId = !programName || qualificationTypeId === QualificationType.MicroQualification ? 0 : programName.educationSubSectorId;

    setProgramName((prev) => ({
      ...prev,
      qualificationTypeId: qualificationTypeId,
      hkoLevelName: hkoLevelName,
      hkoLevelId: hkoLevelId,
      educationSubSectorId: educationSubSectorId
    }));
  };

  const handleSectorChange = (event: any) => {
    const educationSectorId = event.target.value;

    setProgramName((prev) => ({
      ...prev,
      educationSectorId: educationSectorId,
      educationSubSectorId: 0
    }));
  }

  const saveChanges = () => {
    var isValid = doValidations();
    if (isValid) {
      setOpenConfirmModal(true);
    }
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    if (programName.dateTo) {
      let date = new Date(programName.dateTo);
      programName.dateTo = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    }

    if (programName.id) {
      await editProgram(programName.id, programName);
      navigateToDetails();
    }
    else {
      await createProgram(programName);
      navigateToDetails();
    }
  }

  const cancel = () => {
    if (programName.id) {
      navigateToDetails()
    } else {
      navigateToList();
    }
  }

  const navigateToDetails = () => {
    history.push(
      codebookRouteProgramName(),
      {
        page: "details",
        programName: programName
      }
    );
  }

  const navigateToList = () => {
    history.push(
      codebookRouteProgramName()
    );
  }

  const getQualificationTypesSelectData = () => {
    if (!qualificationTypes) {
      return codeBookToSelectData(undefined);
    } else {
      var distinctQualificationTypes: CodeBook[] = [];

      for (let i = 0; i < qualificationTypes.length; i++) {
        if (!distinctQualificationTypes.find(qt => qt.id === qualificationTypes[i].id)) {
          distinctQualificationTypes.push(qualificationTypes[i]);
        }
      }

      return codeBookToSelectData(distinctQualificationTypes);
    }
  };

  return (
    <>
      <Row>
        <Col md={12}>
          <ArrowButton
            backwards
            ariaLabel=""
            onClick={navigateToList}
          />
          <span className={styles.backButtonCaption}>
            {t("codeBook.navigateToCodeBook")}
          </span>

        </Col>
      </Row>
      <Row className={styles.title}>
        <Col md={6}>
          <h3 className={styles.titleWithTooltip}>{t("codeBook.programName")}</h3>
          <Tooltip
            overlay={<div className={styles.tooltipWindow}>{t("codeBook.programNames.note")}</div>}
            placement="right"
          >
            <IconWrapper color="primary">
              <InfoIcon />
            </IconWrapper>
          </Tooltip>
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-right">
          <Button
            outline
            onClick={() => cancel()}
            className={styles.actionButton}>
            {t("common.cancel")}
          </Button>
          <Button className={styles.actionButton}
            onClick={() => saveChanges()}>
            {t("common.save")}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mt-5">
          <StaticField
            label={t("codeBook.programCode")}
            value={(location.state as any).programName?.code!}
          />
        </Col>
        <Col md={8} className="mt-5">
          <TextField
            name={nameof<typeof programName>("name")}
            label={t("common.name")}
            onChange={handleChange(setProgramName)}
            value={programName.name}
            maxLength={512}
            errorText={errors?.name}
          />
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          <Select
            label={t("codeBook.programType")}
            name={nameof<typeof programName>("programTypeId")}
            value={programName.programTypeId}
            readonly={isProgramEditMode}
            onChange={handleProgramTypeChange}
            selectData={codeBookToSelectData(filteredProgramTypes())}
            errorText={errors?.programTypeId}
          />
        </Col>
        <Col md={4}>
          <Select
            label={t("codeBook.qualification")}
            name={nameof<typeof programName>("qualificationTypeId")}
            value={programName.qualificationTypeId}
            readonly={isProgramEditMode}
            onChange={handleQualificationTypeChange}
            selectData={getQualificationTypesSelectData()}
            errorText={errors?.qualificationTypeId}
          />
        </Col>
        {hkoLevels && hkoLevels.length > 2 &&
          <Col md={4}>
            <Select
              label={t("request.hkoLevel")}
              name={nameof<typeof programName>("hkoLevelId")}
              value={programName.hkoLevelId}
              readonly={isProgramEditMode}
              onChange={handleChange(setProgramName)}
              selectData={codeBookToSelectData(hkoLevels)}
              errorText={errors?.hkoLevelId}
            />
          </Col>
        }
        {(!hkoLevels || hkoLevels.length < 2) &&
          <Col md={4}>
            <StaticField
              label={t("request.hkoLevel")}
              value={programName.hkoLevelName}
            />
          </Col>
        }
      </Row>

      <Row>
        <Col md={4}>
          <Select
            label={t("common.educationSector")}
            name={nameof<typeof programName>("educationSectorId")}
            value={programName.educationSectorId}
            readonly={isProgramEditMode}
            onChange={handleSectorChange}
            selectData={codeBookToSelectData(educationSectors)}
            errorText={errors?.educationSectorId}
          />
        </Col>
        {programName && programName.qualificationTypeId !== QualificationType.MicroQualification &&
          <Col md={4}>
            <Select
              label={t("common.educationSubSector")}
              name={nameof<typeof programName>("educationSubSectorId")}
              value={programName.educationSubSectorId}
              readonly={isProgramEditMode}
              onChange={handleChange(setProgramName)}
              selectData={codeBookToSelectData(educationSubSectors)}
              errorText={errors?.educationSubSectorId}
            />
          </Col>
        }
      </Row>

      {programName.programTypeId === ProgramType.ForeignLanguageLearningPrograms &&
        <Row>
          <Col md={4}>
            <Select
              label={t("common.language")}
              name={nameof<typeof programName>("languageId")}
              value={programName.languageId}
              readonly={isProgramEditMode}
              onChange={handleChange(setProgramName)}
              selectData={codeBookToSelectData(languages)}
            />
          </Col>
        </Row>
      }
      <Row>
        <Col md={4} className="mt-5">
          <DatePicker
            name={nameof<typeof programName>("dateTo")}
            label={t("common.dateTo")}
            value={programName.dateTo}
            onChange={handleChange(setProgramName)}
            isClearable={true}
            minYear={new Date().getFullYear() - 25}
            maxYear={new Date().getFullYear() + 25}
          />
        </Col>
      </Row>
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.saveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}